import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "../../../context/CartContext";
import { useLayout } from "../../../context/LayoutContext";
import { useTheme } from "../../../context/ThemeContext";
import { useLang } from "../../../context/LanguageContext";
import { useSearch } from "../../../context/SearchContext";

export default function Navbar() {
	const { totalItems, openDrawer } = useCart();
	const { promoVisible } = useLayout();
	const { isDark } = useTheme();
	const { lang, setLang, t } = useLang();
	const { open: openSearch } = useSearch();
	const [menuOpen, setMenuOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const location = useLocation();

	const navLinks = [
		{ label: t("nav_home"), to: "/" },
		{ label: t("nav_catalog"), to: "/products" },
		// { label: t('nav_build'), to: '/arma-tu-pc', highlight: true },
		{ label: t("nav_services"), to: "/servicios" },
		{ label: t("nav_about"), to: "/nosotros" },
		{ label: t("nav_book"), to: "/agendar", cta: true },
	];

	useEffect(() => {
		const handleScroll = () => setScrolled(window.scrollY > 20);
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const baseClasses = isDark
		? "bg-zinc-950/90 border-zinc-800"
		: "bg-white/90 border-zinc-200";
	const scrolledClasses = scrolled ? "backdrop-blur-xl shadow-lg" : "";
	const textBase = isDark ? "text-zinc-400" : "text-zinc-600";
	const textHover = isDark ? "hover:text-white" : "hover:text-zinc-900";
	const textActive = isDark ? "text-white" : "text-zinc-900";
	const bgActive = isDark ? "bg-zinc-800/60" : "bg-zinc-100";

	return (
		<nav
			className={`fixed left-0 right-0 z-[55] transition-all duration-300 ${baseClasses} ${scrolledClasses}`}
			style={{ top: promoVisible ? "40px" : "0px" }}
		>
			<div className="max-w-screen-xl mx-auto px-3 sm:px-6 flex items-center justify-between h-16">
				{/* Logo */}
				<Link
					to="/"
					className="flex items-center gap-2 flex-shrink-0 cursor-pointer"
				>
					<img
						src="https://storage.readdy-site.link/project_files/e0ad0187-0f87-44ac-bc81-3163fcc0baf7/856a6a4e-c760-477d-a837-65755837c797_fb6affb0-c65b-4aac-81c4-bfd5377e0e55.webp?v=328770f95ae230a12f1326c5fd47e1ad"
						alt="SpartechRD"
						className="h-8 sm:h-9 w-auto object-contain"
						fetchpriority="high"
						loading="eager"
						onError={(e) => {
							(e.target as HTMLImageElement).style.display = "none";
						}}
					/>
					<span
						className={`font-extrabold text-sm sm:text-lg tracking-tight ${isDark ? "text-white" : "text-zinc-900"}`}
					>
						SPARTECHRD
					</span>
				</Link>

				{/* Desktop nav */}
				<div className="hidden lg:flex items-center gap-1">
					{navLinks.map((link) => {
						const isActive = location.pathname === link.to;
						if (link.cta) {
							return (
								<Link
									key={link.to}
									to={link.to}
									className="ml-2 flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors cursor-pointer whitespace-nowrap shadow-md shadow-red-600/20"
								>
									<i className="ri-calendar-line text-sm" />
									{link.label}
								</Link>
							);
						}
						return (
							<Link
								key={link.to}
								to={link.to}
								className={`px-3 py-2 text-sm font-medium tracking-wide rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
									link.highlight
										? "text-red-500 hover:text-red-400"
										: isActive
											? `${textActive} ${bgActive}`
											: `${textBase} ${textHover}`
								}`}
							>
								{link.highlight && <i className="ri-tools-line mr-1 text-xs" />}
								{link.label}
							</Link>
						);
					})}
				</div>

				{/* Right controls */}
				<div className="flex items-center gap-1.5">
					{/* Search */}
					<button
						onClick={openSearch}
						title="Buscar productos"
						className={`hidden md:flex w-9 h-9 items-center justify-center rounded-lg border transition-colors cursor-pointer ${
							isDark
								? "border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
								: "border-zinc-200 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
						}`}
					>
						<i className="ri-search-line text-base" />
					</button>

					{/* Language */}
					<div
						className={`hidden md:flex items-center text-[10px] font-black overflow-hidden rounded-lg border ${isDark ? "border-zinc-700" : "border-zinc-200"}`}
					>
						{(["es", "en"] as const).map((l) => (
							<button
								key={l}
								onClick={() => setLang(l)}
								className={`px-2.5 py-1.5 transition-all cursor-pointer uppercase ${
									lang === l
										? "bg-red-600 text-white"
										: isDark
											? "text-zinc-400 hover:text-white"
											: "text-zinc-500 hover:text-zinc-900"
								}`}
							>
								{l}
							</button>
						))}
					</div>

					{/* Cart */}
					<button
						onClick={openDrawer}
						title="Ver carrito"
						className={`relative w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${
							isDark
								? "border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
								: "border-zinc-200 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
						}`}
					>
						<i className="ri-shopping-cart-line text-base" />
						{totalItems > 0 && (
							<span className="absolute -top-1 -right-1 text-[9px] font-extrabold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 bg-red-600 text-white">
								{totalItems > 99 ? "99+" : totalItems}
							</span>
						)}
					</button>

					{/* Mobile burger */}
					<button
						className={`lg:hidden w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${
							isDark
								? "border-zinc-700 text-zinc-400"
								: "border-zinc-200 text-zinc-500"
						}`}
						onClick={() => setMenuOpen(!menuOpen)}
					>
						<i
							className={
								menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"
							}
						/>
					</button>
				</div>
			</div>

			{/* Mobile menu */}
			{menuOpen && (
				<div
					className={`lg:hidden px-6 py-4 flex flex-col gap-1 border-t ${isDark ? "bg-zinc-950 border-zinc-800" : "bg-white border-zinc-200"}`}
				>
					{/* Mobile search button */}
					<button
						onClick={() => {
							setMenuOpen(false);
							openSearch();
						}}
						className={`w-full flex items-center gap-2 py-2.5 text-sm font-medium tracking-wide cursor-pointer rounded-lg px-3 transition-colors ${
							isDark
								? "text-zinc-400 hover:text-white hover:bg-zinc-800"
								: "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
						}`}
					>
						<i className="ri-search-line" />
						Buscar productos
					</button>

					{navLinks.map((link) => (
						<Link
							key={link.to}
							to={link.to}
							onClick={() => setMenuOpen(false)}
							className={`py-2.5 text-sm font-medium tracking-wide cursor-pointer rounded-lg px-3 transition-colors ${
								link.cta
									? "bg-red-600 text-white text-center font-bold mt-1"
									: link.highlight
										? "text-red-500"
										: isDark
											? "text-zinc-400 hover:text-white hover:bg-zinc-800"
											: "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
							}`}
						>
							{link.label}
						</Link>
					))}
					<div
						className={`flex gap-2 mt-2 pt-2 border-t ${isDark ? "border-zinc-800" : "border-zinc-200"}`}
					>
						{(["es", "en"] as const).map((l) => (
							<button
								key={l}
								onClick={() => setLang(l)}
								className={`flex-1 py-2 text-xs font-black uppercase rounded-lg cursor-pointer transition-colors ${
									lang === l
										? "bg-red-600 text-white"
										: isDark
											? "bg-zinc-800 text-zinc-400"
											: "bg-zinc-100 text-zinc-500"
								}`}
							>
								{l}
							</button>
						))}
					</div>
				</div>
			)}
		</nav>
	);
}
